import { AxiosResponse } from 'axios'

import { Organisation } from '../admin/admin.types'
import { BankDetailsListView } from '../bankDetails/bankDetails.types'

import { PBPGetResponse, PBPListResponse } from '@percent/admin-dashboard/services/pbpResponses'
import { logger } from '@percent/admin-dashboard/common/utility/logger'
import {
  SewaOnboardingServiceCapabilities,
  SewaOnboardingListItem,
  SewaOnboardingListItemEnriched,
  GetSewaOnboardingListProps,
  OnboardOrganisationProps,
  OffboardOrganisationProps
} from './sewaOnboarding.types'

const SEWA_ENRICHMENT_MAX_IDS = 100

const SEWA_BANK_DETAILS_PAGE_SIZE = 50

export const sewaOnboardingService = ({ percentClient }: SewaOnboardingServiceCapabilities) => ({
  getSewaOnboardingList: async (
    params: GetSewaOnboardingListProps
  ): Promise<AxiosResponse<PBPListResponse<SewaOnboardingListItemEnriched>>> => {
    const response = await percentClient.get<PBPListResponse<SewaOnboardingListItem>>(
      'v1/admin/organisations/sewa-onboarding',
      { params }
    )

    if (response.data.data.length === 0) {
      return response as AxiosResponse<PBPListResponse<SewaOnboardingListItemEnriched>>
    }

    const organisationIds = response.data.data.map(item => item.organisationId)

    let orgMap = new Map<string, Organisation>()
    const ifscByOrganisationId = new Map<string, string[]>()

    if (organisationIds.length > SEWA_ENRICHMENT_MAX_IDS) {
      logger(
        new Error(
          `getSewaOnboardingList: skipping enrichment for ${organisationIds.length} ids (max ${SEWA_ENRICHMENT_MAX_IDS}); raise SEWA_ENRICHMENT_MAX_IDS or paginate.`
        )
      )
    } else {
      const [orgsResponse, bankDetailsResponse] = await Promise.all([
        percentClient.get<PBPListResponse<Organisation>>('/v1/admin/organisations', {
          params: { organisationIds, pageSize: organisationIds.length }
        }),
        // IFSC is enrichment behind a separate permission (bank_details_payment_methods:can_list); degrade to empty on failure so the table still renders.
        percentClient
          .get<PBPListResponse<BankDetailsListView>>('v1/admin/bank-details-reviews', {
            params: { organisationIds, pageSize: Math.max(organisationIds.length, SEWA_BANK_DETAILS_PAGE_SIZE) }
          })
          .catch(err => {
            logger(
              err instanceof Error
                ? Object.assign(
                    new Error(`getSewaOnboardingList: bank-details-reviews fetch failed — ${err.message}`),
                    {
                      cause: err
                    }
                  )
                : new Error('getSewaOnboardingList: bank-details-reviews fetch failed')
            )

            return null
          })
      ])

      orgMap = new Map(orgsResponse.data.data.map(org => [org.id, org]))

      // Keep the first (latest, since sorted requested_at desc) review seen per org.
      const bankDetailsReviews = bankDetailsResponse?.data?.data ?? []
      bankDetailsReviews.forEach(review => {
        if (!ifscByOrganisationId.has(review.organisationId)) {
          ifscByOrganisationId.set(review.organisationId, review.ifscCodes ?? [])
        }
      })
    }

    return {
      ...response,
      data: {
        ...response.data,
        data: response.data.data.map(item => {
          const org = orgMap.get(item.organisationId)

          return {
            ...item,
            organisationName: org?.name ?? item.organisationId,
            ifscCodes: ifscByOrganisationId.get(item.organisationId) ?? []
          }
        })
      }
    }
  },
  onboardOrganisation: ({ organisationId }: OnboardOrganisationProps) =>
    percentClient.post<PBPGetResponse<SewaOnboardingListItem>>(
      `v1/admin/organisations/sewa-onboarding/${organisationId}`
    ),
  offboardOrganisation: ({ organisationId }: OffboardOrganisationProps) =>
    percentClient.delete(`v1/admin/organisations/sewa-onboarding/${organisationId}`)
})
